import React, { useState } from 'react';
import { X, Save, Loader2, AlertCircle } from 'lucide-react';
import { useClients } from '../../hooks/useClients';
import type { Matter, MatterFormData, UpdateMatterRequest } from '../../types';

interface MatterFormProps {
  matter?: Matter; // If provided, we're editing; otherwise creating
  clientId?: string; // Optional pre-selected client (e.g., when creating from client page)
  isLoading?: boolean;
  onSubmit: (data: MatterFormData | UpdateMatterRequest) => Promise<void>;
  onCancel: () => void;
  className?: string;
}

const MatterForm: React.FC<MatterFormProps> = ({
  matter,
  clientId: preSelectedClientId,
  isLoading = false,
  onSubmit,
  onCancel,
  className = ''
}) => {
  const { clients, isLoading: clientsLoading } = useClients();
  
  const [formData, setFormData] = useState<MatterFormData>({
    clientId: matter?.clientId || preSelectedClientId || '',
    title: matter?.title || '',
    type: matter?.type || '',
    status: matter?.status || 'active',
    description: matter?.description || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Common matter types for the dropdown
  const matterTypes = [
    'Corporate Law',
    'Employment Law',
    'Intellectual Property',
    'Real Estate',
    'Contract Law',
    'Litigation',
    'Family Law',
    'Tax Law',
    'Immigration Law',
    'Criminal Law',
    'Personal Injury',
    'Environmental Law',
    'Securities Law',
    'Bankruptcy Law',
    'Other'
  ];

  // Status options
  const statusOptions = [
    { value: 'active', label: 'Active', description: 'Matter is currently being worked on' },
    { value: 'completed', label: 'Completed', description: 'Matter has been completed' },
    { value: 'on-hold', label: 'On Hold', description: 'Matter is temporarily paused' },
    { value: 'cancelled', label: 'Cancelled', description: 'Matter has been cancelled' }
  ];

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Client validation - required when creating new matter without pre-selected client
    if (!matter && !preSelectedClientId && !formData.clientId.trim()) {
      newErrors.clientId = 'Client selection is required';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Matter title is required';
    } else if (formData.title.length < 3) {
      newErrors.title = 'Matter title must be at least 3 characters';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Matter title must be less than 200 characters';
    }

    if (!formData.type.trim()) {
      newErrors.type = 'Matter type is required';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      // Error handling is done by parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleChange = (field: keyof MatterFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle Enter key in form
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e as any);
    }
  };

  const isEditing = !!matter;
  const title = isEditing ? 'Edit Matter' : 'Create New Matter';
  const submitText = isEditing ? 'Update Matter' : 'Create Matter';

  return (
    <div className={`bg-gray-800 border border-gray-700 rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-700">
        <h2 className="text-xl font-semibold text-white">{title}</h2>
        <button
          onClick={onCancel}
          disabled={isSubmitting}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="p-6 space-y-6">
        {/* Client Selection - Only show when creating new matter without pre-selected client */}
        {!matter && !preSelectedClientId && (
          <div>
            <label htmlFor="clientId" className="block text-sm font-medium text-gray-300 mb-2">
              Client *
            </label>
            <select
              id="clientId"
              value={formData.clientId}
              onChange={(e) => handleChange('clientId', e.target.value)}
              disabled={isSubmitting || clientsLoading}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                errors.clientId ? 'border-red-500' : 'border-gray-600'
              }`}
            >
              <option value="">Select a client...</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} {client.company ? `(${client.company})` : ''}
                </option>
              ))}
            </select>
            {errors.clientId && (
              <div className="flex items-center mt-1 text-sm text-red-400">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.clientId}
              </div>
            )}
            {clientsLoading && (
              <div className="flex items-center mt-1 text-sm text-gray-400">
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Loading clients...
              </div>
            )}
          </div>
        )}

        {/* Matter Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
            Matter Title *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            disabled={isSubmitting}
            className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
              errors.title ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="Enter matter title..."
            maxLength={200}
          />
          {errors.title && (
            <div className="flex items-center mt-1 text-sm text-red-400">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.title}
            </div>
          )}
        </div>

        {/* Matter Type */}
        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-300 mb-2">
            Matter Type *
          </label>
          <select
            id="type"
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
            disabled={isSubmitting}
            className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
              errors.type ? 'border-red-500' : 'border-gray-600'
            }`}
          >
            <option value="">Select matter type...</option>
            {matterTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          {errors.type && (
            <div className="flex items-center mt-1 text-sm text-red-400">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.type}
            </div>
          )}
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-300 mb-2">
            Status
          </label>
          <select
            id="status"
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value as Matter['status'])}
            disabled={isSubmitting}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">
            {statusOptions.find(opt => opt.value === formData.status)?.description}
          </p>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
            Description
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            disabled={isSubmitting}
            rows={4}
            className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-none ${
              errors.description ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="Enter matter description..."
            maxLength={1000}
          />
          <div className="flex justify-between items-center mt-1">
            {errors.description ? (
              <div className="flex items-center text-sm text-red-400">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.description}
              </div>
            ) : (
              <div></div>
            )}
            <span className="text-sm text-gray-500">
              {(formData.description || '').length}/1000
            </span>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSubmitting || isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {submitText}
              </>
            )}
          </button>
        </div>

        {/* Keyboard Shortcut Hint */}
        <div className="text-xs text-gray-500 text-center pt-2">
          Press <kbd className="px-1 py-0.5 bg-gray-700 rounded text-gray-300">Ctrl+Enter</kbd> to submit
        </div>
      </form>
    </div>
  );
};

export default MatterForm;
